/**
 * VendorSettingsPage.jsx — /vendor/settings — vendor's own shop settings.
 *
 * iter254: introduces the Shop Visibility switch requested by the
 * founder — vendors can flip between "members_only" (login required
 * to browse) and "public" (anyone, including search engines, can
 * browse the catalogue). Checkout is always login-gated regardless.
 *
 * Reads / writes:
 *   GET /api/vendors/me
 *   PUT /api/vendors/me   { visibility: "public" | "members_only" }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Globe, Lock, ShieldCheck, Loader2 } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function VendorSettingsPage() {
  const [vendor, setVendor] = useState(null);
  const [err, setErr]       = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Shop settings · HustleSuite";
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vendors/me`, {
          headers: authHeader(), withCredentials: true,
        });
        setVendor(data);
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        setErr(detail);
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate(`/login?next=${encodeURIComponent("/vendor/settings")}`, { replace: true });
        }
      }
    })();
  }, [navigate]);

  const flipVisibility = async (next) => {
    if (!vendor || saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/vendors/me`,
        { visibility: next },
        { headers: authHeader(), withCredentials: true },
      );
      setVendor(data);
      toast.success(next === "public"
        ? "Shop is now public — anyone can browse. Checkout still requires login."
        : "Shop is now members-only — visitors must log in to browse.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update visibility");
    } finally {
      setSaving(false);
    }
  };

  if (err && !vendor) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-[#0a0908] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="vendor-settings-error">
          <p className="text-lg font-black">Couldn't load shop</p>
          <p className="mt-2 text-stone-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!vendor) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400"
           data-testid="vendor-settings-loading">
        <Loader2 className="animate-spin mr-2" size={16}/> Loading…
      </div>
    );
  }

  const visibility = vendor.visibility || "members_only";
  const isPublic = visibility === "public";

  return (
    <div
      data-testid="vendor-settings-page"
      className="min-h-screen bg-stone-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100"
    >
      <PlatformHeader
        brand={{ name: vendor.business_name || "Shop settings", to: "/vendor/settings" }}
        showBookCta={false}
        showCart={false}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <header className="mb-8">
          <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
            Shop settings
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black leading-tight">
            {vendor.business_name}
          </h1>
          <p className="mt-2 text-sm text-stone-500 max-w-xl">
            Manage who can see your catalogue and how it appears on the Plaza.
          </p>
        </header>

        <section
          data-testid="vendor-visibility-card"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 sm:p-8 mb-6"
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center ${
              isPublic
                ? "bg-emerald-500/10 text-emerald-500"
                : "bg-amber-500/10 text-amber-500"
            }`}>
              {isPublic ? <Globe size={22}/> : <Lock size={22}/>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-amber-500">
                Shop visibility
              </p>
              <h2 className="mt-1 text-xl font-black">
                {isPublic ? "Public shop" : "Members-only shop"}
              </h2>
              <p className="mt-2 text-sm text-stone-600 dark:text-stone-300 leading-relaxed">
                {isPublic ? (
                  <>Anyone can browse your catalogue — including search engines and
                     shoppers who haven't signed up yet. This is the recommended
                     setting for maximum discovery.</>
                ) : (
                  <>Only signed-in members can browse your catalogue. Great for
                     wholesale or private-buyer shops. New shops default to this
                     setting.</>
                )}
              </p>
              <p className="mt-3 text-[13px] text-stone-500 inline-flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-500"/>
                Checkout and payment always require a signed-in member — regardless of visibility.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="vendor-visibility-public"
              onClick={() => flipVisibility("public")}
              disabled={saving || isPublic}
              className={`px-5 py-2.5 rounded-full font-black text-sm inline-flex items-center gap-2 transition ${
                isPublic
                  ? "bg-emerald-500 text-slate-950"
                  : "bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-stone-200 hover:bg-emerald-500/20 hover:text-emerald-700"
              } ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <Globe size={14}/> Public
            </button>
            <button
              type="button"
              data-testid="vendor-visibility-members"
              onClick={() => flipVisibility("members_only")}
              disabled={saving || !isPublic}
              className={`px-5 py-2.5 rounded-full font-black text-sm inline-flex items-center gap-2 transition ${
                !isPublic
                  ? "bg-amber-500 text-slate-950"
                  : "bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-stone-200 hover:bg-amber-500/20 hover:text-amber-700"
              } ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <Lock size={14}/> Members only
            </button>
            {saving && (
              <span className="inline-flex items-center gap-1 text-xs text-stone-500 self-center">
                <Loader2 size={12} className="animate-spin"/> Saving…
              </span>
            )}
          </div>
        </section>

        <p className="text-xs text-stone-500 mt-4">
          Shop slug: <code className="text-stone-700 dark:text-stone-300">{vendor.shop_slug}</code>
          &nbsp;·&nbsp;
          Status: <code className="text-stone-700 dark:text-stone-300">{vendor.status}</code>
        </p>
      </div>

      <SuiteFooter />
    </div>
  );
}
